import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { del, get, patch, post, put } from '@agribot/polygon-checks-ui'
import { query } from '@agribot/polygon-checks-ui'
import type {
  AdminKey, AdminOrganisation, BillingUsage, KeyUsers, ProvisionedUser, RequestLogPage, Subscription, UsageResponse,
} from './types'

/**
 * The admin screens' data: a partner root key's users, usage, and the
 * platform's keys. Kept out of api/hooks.ts so the partner edition, which
 * has no admin section, carries none of it.
 */
const keys = {
  usage: (scope: string, q: string) => ['usage', scope, q] as const,
  adminKeys: (q: string) => ['admin-keys', q] as const,
  adminOrganisations: ['admin-organisations'] as const,
  adminKeyUsers: (id: string) => ['admin-key-users', id] as const,
}

/** The users this organisation's keys have provisioned. Needs a root key. */
export function useProvisionedUsers(params: Record<string, string | undefined> = {}) {
  const q = query(params)
  return useQuery({
    queryKey: ['provisioned-users', q],
    queryFn: () =>
      get<{ users: ProvisionedUser[]; total: number }>(`/users${q ? `?${q}` : ''}`),
    placeholderData: (prev) => prev,
  })
}

/** Mints a user. The token comes back once and is never obtainable again. */
export function useProvisionUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (note?: string) =>
      post<{ id: string; organisation_id: string; token: string }>(
        '/users', note ? { note } : {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provisioned-users'] }),
  })
}

/** The note is the only editable thing about a user, because it is the only
 *  thing about a user we did not refuse to store. */
export function useSetUserNote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string | null }) =>
      patch<{ id: string; note: string | null }>(`/users/${id}`, { note }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provisioned-users'] }),
  })
}

export function useRevokeProvisionedUser() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => post<{ id: string; token: string }>(`/users/${id}/tokens`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['provisioned-users'] }),
  })
}

/**
 * Usage.
 *
 * Two endpoints behind one hook, because the screen is the same screen: an
 * operator sees their own organisation, a platform operator can widen it to
 * every organisation. Which one the caller may have is decided by the API —
 * `/admin/usage` answers 403 to anyone else — so the scope here only chooses
 * what to ask for.
 */
export function useUsage(
  params: Record<string, string | undefined> = {},
  scope: 'organisation' | 'platform' = 'organisation',
) {
  const q = query(params)
  return useQuery({
    queryKey: keys.usage(scope, q),
    queryFn: () =>
      get<UsageResponse>(
        `${scope === 'platform' ? '/admin/usage' : '/usage'}${q ? `?${q}` : ''}`,
      ),
    placeholderData: (prev) => prev,
  })
}

export function useAdminKeys(params: Record<string, string | undefined> = {}) {
  const q = query(params)
  return useQuery({
    queryKey: keys.adminKeys(q),
    queryFn: () => get<{ keys: AdminKey[] }>(`/admin/keys${q ? `?${q}` : ''}`).then((r) => r.keys),
    // "Show revoked" changes the key, and without this the table empties
    // and refills rather than gaining a few rows.
    placeholderData: (prev) => prev,
  })
}

export const useAdminOrganisations = () =>
  useQuery({
    queryKey: keys.adminOrganisations,
    queryFn: () =>
      get<{ organisations: AdminOrganisation[] }>('/admin/organisations')
        .then((r) => r.organisations),
    staleTime: 60_000,
  })

/**
 * Mints a key. The secret comes back once and is never obtainable again, so
 * the caller has to show it — the cache deliberately does not keep it.
 */
export function useCreateAdminKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string; organisation_id?: string; scope?: string }) =>
      post<{ key: AdminKey; secret: string }>('/admin/keys', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-keys'] })
      qc.invalidateQueries({ queryKey: ['admin-organisations'] })
    },
  })
}

/**
 * Onboarding a partner starts here: the organisation is the boundary every
 * other rule is drawn around, so it exists before their key does.
 */
/** What one key has provisioned — the answer to "what did this key create?" */
export const useAdminKeyUsers = (id: string | undefined) =>
  useQuery({
    queryKey: keys.adminKeyUsers(id ?? ''),
    queryFn: () => get<KeyUsers>(`/admin/keys/${id}/users`),
    enabled: Boolean(id),
  })

export function useCreateOrganisation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: { name: string }) =>
      post<AdminOrganisation>('/admin/organisations', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-organisations'] }),
  })
}

/**
 * The request log, newest first, a page at a time. Each page asks for what
 * came before the last one's oldest row, so rows arriving meanwhile cannot
 * shift a page and show a request twice.
 */
export function useRequestLogs(params: Record<string, string | undefined> = {}) {
  const q = query(params)
  return useInfiniteQuery({
    queryKey: ['admin-logs', q],
    queryFn: ({ pageParam }) => {
      const page = query({ ...params, before: pageParam === null ? undefined : String(pageParam) })
      return get<RequestLogPage>(`/admin/logs${page ? `?${page}` : ''}`)
    },
    initialPageParam: null as number | null,
    getNextPageParam: (last) => last.next_before,
    placeholderData: (prev) => prev,
  })
}

export function useRevokeAdminKey() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      del<{ id: string; revoked_at: string }>(`/admin/keys/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-keys'] })
      qc.invalidateQueries({ queryKey: ['admin-organisations'] })
    },
  })
}

/** A billing period as the API takes it: nothing (the subscription year), a month, or a custom range. */
export type BillingPeriodQuery = { month?: string; from?: string; to?: string }

/** What one organisation's subscription bills against. Platform admins only. */
export function useBillingUsage(organisationId: string | null, period: BillingPeriodQuery) {
  const q = query(period)
  return useQuery({
    queryKey: ['billing-usage', organisationId, q],
    queryFn: () => get<BillingUsage>(`/admin/organisations/${organisationId}/billing-usage${q ? `?${q}` : ''}`),
    enabled: Boolean(organisationId),
    placeholderData: (prev) => prev,
  })
}

/** The billed figures for every organisation. */
export function useAllBillingUsage(period: BillingPeriodQuery, enabled = true) {
  const q = query(period)
  return useQuery({
    queryKey: ['billing-usage', 'all', q],
    queryFn: () => get<{ items: BillingUsage[] }>(`/admin/billing-usage${q ? `?${q}` : ''}`).then((r) => r.items),
    enabled,
    placeholderData: (prev) => prev,
  })
}

export function useSetSubscription(organisationId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Subscription) =>
      put<{ organisationId: string; subscription: Subscription }>(`/admin/organisations/${organisationId}/subscription`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-usage'] }),
  })
}
